import { IDBService } from './indexdb/idb.service';
import { AppConstants } from '../constants/app-constants';
import { Injectable } from '@angular/core';
import { DomStorageFallbackService } from './DomStorageFallback.service';

/**
 * Utility Service to be used by multiple modules to store/cache any data needed by front end view.
 */
// @dynamic
@Injectable()
export class UIStorageService {
    static lr_json_data;

    /* istanbul ignore next */
    constructor(private idbService: IDBService, private domStorageFB: DomStorageFallbackService) {
        try {
            UIStorageService.lr_json_data = JSON.parse(this.domStorageFB.getItem('lr_json_data'));
        } catch (error) { }
    }

    /* istanbul ignore next */
    private checkLRJsonData() {
        if (UIStorageService.lr_json_data === null) {
            UIStorageService.lr_json_data = JSON.parse(this.domStorageFB.getItem('lr_json_data'));
        }
    }

    /* istanbul ignore next */
    public getClientId() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.clientid;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getCompanyId() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.companyid;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getLineage() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.lineage;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getOrgId() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.orgid;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getGroupId() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.groupid;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getUser() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.userDetails;
        } catch (error) { return null; }
    }

    /* istanbul ignore next */
    public getToken(key: string) {
        let value: any;

        // search in sessionStorage
        value = this.domStorageFB.getItem(key);
        // search in localStorage
        if (value === null) {
            value = this.domStorageFB.getItem(key, 'localStorage');
        }
        return value;
    }

    /* get method  */
    /* istanbul ignore next */
    public get(key: string, callbackFn: any) {
        let value: any;

        // search in sessionStorage
        value = this.domStorageFB.getItem(key);
        if (value) {
            callbackFn(value);
        }

        // search in localStorage
        if (value === null) {
            value = this.domStorageFB.getItem(key, 'localStorage');
            if (value) {
                callbackFn(value);
            }
        }

        // search in indexeddb
        if (value === null) {
            this.idbService.getRecordFromObjStore(AppConstants.APP_INIT_TABLE, key)
                .toPromise()
                .then((data) => {
                    callbackFn(data);
                }, (error) => {
                    callbackFn(error);
                });
        }

    }

    /* add / update method */
    /* istanbul ignore next */
    public add(key: string, value: any) {
        // check if key is object
        if (typeof (value) === 'object') {
            value = JSON.stringify(value);
        }

        if (this.sessionStorageAdd(key, value)) {
            return true;
        } else if (this.localStorageAdd(key, value)) {
            return true;
        } else if (this.dbStorageAdd(key, value)) {
            return true;
        } else {
            return false;
        }
    }

    /* add / update method */
    /* istanbul ignore next */
    public addToken(key: string, value: any) {
        // check if key is object
        if (typeof (value) === 'object') {
            value = JSON.stringify(value);
        }
        if (this.sessionStorageAdd(key, value)) {
            return true;
        } else if (this.localStorageAdd(key, value)) {
            return true;
        } else {
            return false;
        }
    }

    /* add method : session storage */
    /* istanbul ignore next */
    private sessionStorageAdd(key: string, value: any) {
        try {
            this.domStorageFB.setItem(key, value);
        } catch (e) {
            if (e.code === DOMException.QUOTA_EXCEEDED_ERR) {
            }
            return false;
        }
        return true;
    }

    /* add method : local storage */
    /* istanbul ignore next */
    private localStorageAdd(key: string, value: any) {
        try {
            this.domStorageFB.setItem(key, value, 'localStorage');
        } catch (e) {
            if (e.code === DOMException.QUOTA_EXCEEDED_ERR) {
            }
            return false;
        }
        return true;
    }

    /* add method : indexeddb */
    /* istanbul ignore next */
    private dbStorageAdd(key: string, value: any) {
        try {
            this.idbService.addRecordsInObjStore(AppConstants.APP_INIT_TABLE, value, key)
                .toPromise()
                .then((data) => {
                }, (error) => {
                    this.idbService.editRecord(AppConstants.APP_INIT_TABLE, value, key)
                        .toPromise()
                        .then((data) => {
                        }, (err) => {
                        });
                });
        } catch (e) {
            if (e.code === DOMException.QUOTA_EXCEEDED_ERR) {
            }
            return false;
        }
        return true;
    }

    /* delete method */
    /* istanbul ignore next */
    public async delete(key: string) {
        if (this.domStorageFB.getItem(key)) {
            this.domStorageFB.removeItem(key);
            return true;
        } else if (this.domStorageFB.getItem(key, 'localStorage')) {
            this.domStorageFB.removeItem(key, 'localStorage');
            return true;
        } else if (this.idbService.getRecordFromObjStore(AppConstants.APP_INIT_TABLE, key)) {
            this.idbService.deleteRecordAsync(AppConstants.APP_INIT_TABLE, key)
                .toPromise()
                .then((x) => {
                    return true;
                });
        } else {
            return false;
        }
    }

    /* delete all method
    public deleteAll() {
        if (sessionStorage.length > 0) {
            Object.keys(sessionStorage)
                .forEach(function (k) {
                    sessionStorage.removeItem(k);
                });
            return true;
        }
        if (localStorage.length > 0) {
            Object.keys(localStorage)
                .forEach(function (k) {
                    localStorage.removeItem(k);
                });
            return true;
        }
        // tslint:disable-next-line:one-line
        else {
            this.idbService.clearObjectStore(AppConstants.APP_INIT_TABLE)
                .toPromise()
                .then((x) => {
                    return true;
                });
        }
    } */

    /**
      * This method strips the passed values from json object key's value.
      *
       * @param attrName
      * @param attrValue
      * @param jsonObject
       */

    /* istanbul ignore next */
    public stripAttrFromJson(tobeRemoved: string[], attrName, jsonObject) {
        try {
            if (jsonObject !== null) {
                let channelReqData = jsonObject[attrName];

                for (let i = 0; i < tobeRemoved.length; i++) {
                    channelReqData = this.replaceKeyValue(tobeRemoved[i], channelReqData);
                }
                return channelReqData;
            }
        } catch (error) {
        }
    }

    /* istanbul ignore next */
    public replaceKeyValue(element, channelReqData) {
        try {
            let start = channelReqData.indexOf(element);
            if (start !== -1) {
                let end = channelReqData.indexOf('&', start) + 1;
                if (end === 0) {
                    end = channelReqData.indexOf('::', start);
                }
                if (end > 0) {
                    let substr = channelReqData.substring(start, end);
                    let replacedStr = channelReqData.replace(substr, '');
                    return replacedStr;
                }
            }
            return channelReqData;
        } catch (error) {
        }
    }

    /* istanbul ignore next */
    public addAttributeToJsonObject(attrName, attrValue, jsonObject) {
        try {
            if (jsonObject !== null) {
                jsonObject[attrName] = attrValue;
                return jsonObject;
            }
        } catch (error) {
        }
    }

    /* istanbul ignore next */
    public uuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            // tslint:disable-next-line:no-bitwise
            let r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }


    // added for Getting Locale details from session storage.

    /* istanbul ignore next */
    public getLocale() {
        try {
            this.checkLRJsonData();
            return UIStorageService.lr_json_data.locale;
        } catch (error) { return null; }
    }


    /* get method for retriving value from Browser storage, via passed key */

    /* istanbul ignore next */
    public fetchStorageValueByKey(key: string): any {
        let value: any;

        // search in sessionStorage
        value = this.domStorageFB.getItem(key);
        if (value) {
            return value;
        }

        // search in localStorage
        if (value === null) {
            value = this.domStorageFB.getItem(key, 'localStorage');
            if (value) {
                return value;
            }
        }

        // search in indexeddb
        if (value === null) {
            this.idbService.getRecordFromObjStore(AppConstants.APP_INIT_TABLE, key)
                .toPromise()
                // UIStorageService.db.getByKey('UIStore', key)
                .then((data) => {
                    return data;
                }, (error) => {
                    return error;
                });
        }

    }
}
